'use client';

import clsx from 'clsx';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';

import {
  useDeleteChatMessageMutation,
  useLazyGetDirectChatMessagesQuery,
  useLazyGetChatMessageQuery,
  useLazyGetGeneralChatMessagesQuery,
  useLazyGetJobChatMessagesQuery,
  useUpdateChatMessageMutation,
} from '@/features/chat/api/chatApi';
import { chatApi } from '@/features/chat/api/chatApi';
import { WorkspaceMemberHoverCard } from '@/features/workspaces/components';
import { useAppDispatch } from '@/store/hooks';
import {
  Avatar,
  ConfirmActionModal,
  OptionsMenu,
  RichTextEditor,
} from '@web/ui';
import { formatDate, getErrorMessage, notify, sanitizeHtml } from '@web/utils';

import styles from './ChatPanel.module.scss';

import type { ActiveChat } from '@/app/(protected)/[workspace_slug]/chat/ChatContext';
import type { ChatMessage } from '@/features/chat/types';
import type { RichTextEditorValue } from '@web/ui';
import type { MouseEvent } from 'react';

const USER_PROFILE_PATH_REGEX =
  /\/users\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})(?:[/?#]|$)/i;

function getAuthorName(message: ChatMessage) {
  const user = message.author_user;
  if (!user) {
    return message.author_type === 'system' ? 'Net Net' : 'Unknown user';
  }

  return (
    user.display_name ||
    `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim() ||
    user.email ||
    'Unknown user'
  );
}

function escapeHtml(value: string) {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
}

function escapeAttribute(value: string) {
  return escapeHtml(value);
}

function getMentionUserLabel(message: ChatMessage, userId: string) {
  return (
    message.mentions.users[userId]?.display_name ??
    Object.values(message.mentions.users).find((user) => user.id === userId)
      ?.display_name ??
    userId
  );
}

function getWorkReferenceLabel(
  message: ChatMessage,
  targetType: string,
  targetId: string,
) {
  return (
    message.mentions.work[`${targetType}:${targetId}`]?.label ??
    Object.values(message.mentions.work).find(
      (work) => work.target_type === targetType && work.target_id === targetId,
    )?.label ??
    `${targetType}:${targetId}`
  );
}

function renderCanonicalTokensForEditor(message: ChatMessage, body: string) {
  return body
    .replace(/@\[([0-9a-f-]{36})\]/gi, (_match, userId: string) => {
      const canonicalValue = `@[${userId}]`;
      const label = `@${getMentionUserLabel(message, userId)}`;

      return `<span data-rich-text-token="${escapeAttribute(
        canonicalValue,
      )}">${escapeHtml(label)}</span>`;
    })
    .replace(
      /\[\[(job|deliverable|task):([0-9a-f-]{36})\]\]/gi,
      (_match, targetType: string, targetId: string) => {
        const canonicalValue = `[[${targetType}:${targetId}]]`;
        const label = `[[${getWorkReferenceLabel(
          message,
          targetType,
          targetId,
        )}]]`;

        return `<span data-rich-text-token="${escapeAttribute(
          canonicalValue,
        )}">${escapeHtml(label)}</span>`;
      },
    );
}

function getEditableMessageBody(message: ChatMessage) {
  if (message.body_format === 'html') {
    return renderCanonicalTokensForEditor(message, message.body);
  }

  return `<p>${escapeHtml(message.body_text || message.body)}</p>`;
}

function getClipboardHtml(message: ChatMessage) {
  if (message.body_format === 'html') {
    return renderCanonicalTokensForEditor(message, message.body);
  }

  return `<p>${escapeHtml(message.body_text || message.body)}</p>`;
}

async function writeMessageToClipboard(message: ChatMessage) {
  if (!navigator.clipboard) {
    throw new Error('Clipboard unavailable');
  }

  const clipboardWithRichWrite = navigator.clipboard as Clipboard & {
    write?: (data: ClipboardItem[]) => Promise<void>;
  };

  if (clipboardWithRichWrite.write && typeof ClipboardItem !== 'undefined') {
    await clipboardWithRichWrite.write([
      new ClipboardItem({
        'text/html': new Blob([getClipboardHtml(message)], {
          type: 'text/html',
        }),
        'text/plain': new Blob([message.body_text], {
          type: 'text/plain',
        }),
      }),
    ]);
    return;
  }

  await navigator.clipboard.writeText(message.body_text);
}

const CANONICAL_WORK_REFERENCE_REGEX =
  /^\[\[(job|deliverable|task):[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\]\]$/i;

function hasInvalidWorkReferenceToken(value: string) {
  const tokens = value.match(/\[\[[^\]]+\]\]/g) ?? [];

  return tokens.some((token) => !CANONICAL_WORK_REFERENCE_REGEX.test(token));
}

function formatMessageTimestamp(value: string) {
  const timestamp = dayjs(value);
  const now = dayjs();

  if (timestamp.isSame(now, 'day')) {
    return formatDate(value, 'h:mm A');
  }

  if (timestamp.isSame(now, 'year')) {
    return formatDate(value, 'dddd, MMM D');
  }

  return formatDate(value, 'MMM D, YYYY');
}

function compareMessagesByCreatedAt(left: ChatMessage, right: ChatMessage) {
  return (
    new Date(left.created_at).getTime() - new Date(right.created_at).getTime()
  );
}

function mergeReplies(...replySets: ChatMessage[][]) {
  const byId = new Map<string, ChatMessage>();

  replySets.flat().forEach((reply) => {
    byId.set(reply.id, reply);
  });

  return Array.from(byId.values()).sort(compareMessagesByCreatedAt);
}

function getSourceLabel(message: ChatMessage) {
  if (message.source.conversation_type === 'job_channel') {
    return message.source.job?.name ?? message.source.title;
  }

  return message.source.title;
}

function getSourceActiveChat(message: ChatMessage): ActiveChat {
  return {
    id: message.source.id,
    type: message.source.conversation_type,
    title: getSourceLabel(message),
    description:
      message.source.conversation_type === 'job_channel'
        ? 'Job channel'
        : undefined,
    jobId: message.source.job?.id,
    isArchived: message.source.is_archived,
    isReadOnly: message.source.is_read_only,
  };
}

function getMessageListTags(message: ChatMessage) {
  const tags = [
    { type: 'ChatMessages' as const, id: 'STREAM' },
    { type: 'ChatMessages' as const, id: 'UNREAD' },
    { type: 'ChatMessages' as const, id: 'MENTIONS' },
    { type: 'ChatMessages' as const, id: `MESSAGE:${message.id}` },
    { type: 'ChatDestinations' as const, id: 'LIST' },
  ];

  if (message.source.conversation_type === 'general_channel') {
    tags.push({ type: 'ChatMessages' as const, id: 'GENERAL' });
  }

  if (message.source.conversation_type === 'direct_message') {
    tags.push({ type: 'ChatMessages' as const, id: `DM:${message.source.id}` });
  }

  if (
    message.source.conversation_type === 'job_channel' &&
    message.source.job
  ) {
    tags.push({
      type: 'ChatMessages' as const,
      id: `JOB:${message.source.job.id}`,
    });
  }

  return tags;
}

function isCurrentUserMessageAuthor({
  message,
  currentUserId,
  currentUserEmail,
}: {
  message: ChatMessage;
  currentUserId?: string | null;
  currentUserEmail?: string | null;
}) {
  return Boolean(
    message.author_user &&
      ((currentUserId && message.author_user.id === currentUserId) ||
        (currentUserEmail && message.author_user.email === currentUserEmail)),
  );
}

function getUserMentionIdFromClickTarget(target: EventTarget | null) {
  if (!(target instanceof HTMLElement)) {
    return null;
  }

  const anchor = target.closest<HTMLAnchorElement>('a[href]');
  if (!anchor) {
    return null;
  }

  const mentionId = anchor.dataset.mentionId;
  if (anchor.dataset.mentionType === 'user' && mentionId) {
    return mentionId;
  }

  return anchor.href.match(USER_PROFILE_PATH_REGEX)?.[1] ?? null;
}

function handleUserMentionClick({
  event,
  onOpenUserProfile,
}: {
  event: MouseEvent<HTMLElement>;
  onOpenUserProfile?: (userId: string) => void;
}) {
  if (!onOpenUserProfile) {
    return;
  }

  const userId = getUserMentionIdFromClickTarget(event.target);
  if (!userId) {
    return;
  }

  event.preventDefault();
  event.stopPropagation();
  onOpenUserProfile(userId);
}

function ChatReplyPreview({
  message,
  currentUserId,
  currentUserEmail,
  onOpenDirectMessage,
  onOpenUserProfile,
}: {
  message: ChatMessage;
  currentUserId?: string | null;
  currentUserEmail?: string | null;
  onOpenDirectMessage?: (userId: string) => void;
  onOpenUserProfile?: (userId: string) => void;
}) {
  const authorName = getAuthorName(message);
  const html = sanitizeHtml(message.body_html);
  const isDeleted = message.state === 'deleted';
  const isCurrentUserAuthor = isCurrentUserMessageAuthor({
    message,
    currentUserId,
    currentUserEmail,
  });

  return (
    <div className={styles.chatPanel__replyPreview}>
      <Avatar
        size="small"
        name={authorName}
        src={message.author_user?.avatar_url ?? message.author_user?.avatar}
      />
      <div>
        <div className={styles.chatPanel__replyPreviewMeta}>
          {message.author_user ? (
            <WorkspaceMemberHoverCard
              user={message.author_user}
              canMessage={!isCurrentUserAuthor}
              onMessage={isCurrentUserAuthor ? undefined : onOpenDirectMessage}
            >
              <strong>{authorName}</strong>
            </WorkspaceMemberHoverCard>
          ) : (
            <strong>{authorName}</strong>
          )}
          <span>{formatMessageTimestamp(message.created_at)}</span>
        </div>
        <div
          className={clsx(
            styles.chatPanel__replyPreviewBody,
            isDeleted && styles.chatPanel__deletedMessageBody,
          )}
          onClick={(event) =>
            handleUserMentionClick({ event, onOpenUserProfile })
          }
          dangerouslySetInnerHTML={{ __html: html }}
        />
      </div>
    </div>
  );
}

export function ChatMessageItem({
  message,
  onReply,
  showSource = false,
  onSelectSource,
  currentUserId,
  currentUserEmail,
  onOpenDirectMessage,
  onOpenUserProfile,
}: {
  message: ChatMessage;
  onReply: (message: ChatMessage) => void;
  showSource?: boolean;
  onSelectSource?: (chat: ActiveChat) => void;
  currentUserId?: string | null;
  currentUserEmail?: string | null;
  onOpenDirectMessage?: (userId: string) => void;
  onOpenUserProfile?: (userId: string) => void;
}) {
  const dispatch = useAppDispatch();
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(message.body_html);
  const [loadedReplies, setLoadedReplies] = useState<ChatMessage[] | null>(
    null,
  );
  const [loadMessage, messageDetailState] = useLazyGetChatMessageQuery();
  const [loadGeneralReplies, generalRepliesState] =
    useLazyGetGeneralChatMessagesQuery();
  const [loadJobReplies, jobRepliesState] = useLazyGetJobChatMessagesQuery();
  const [loadDirectReplies, directRepliesState] =
    useLazyGetDirectChatMessagesQuery();
  const [updateMessage, updateMessageState] = useUpdateChatMessageMutation();
  const [deleteMessage, deleteMessageState] = useDeleteChatMessageMutation();
  const authorName = getAuthorName(message);
  const html = useMemo(
    () => sanitizeHtml(message.body_html),
    [message.body_html],
  );
  const detail = messageDetailState.data?.data;
  const canManageMessage = detail?.can_edit ?? message.can_edit ?? true;
  const isDeleted = message.state === 'deleted';
  const sourceLabel = getSourceLabel(message);
  const isCurrentUserAuthor = isCurrentUserMessageAuthor({
    message,
    currentUserId,
    currentUserEmail,
  });
  const replies = useMemo(
    () =>
      loadedReplies
        ? mergeReplies(message.replies, loadedReplies)
        : mergeReplies(message.replies),
    [loadedReplies, message.replies],
  );
  const displayedReplyCount = Math.max(message.reply_count, replies.length);
  const remainingReplyCount = Math.max(displayedReplyCount - replies.length, 0);
  const isLoadingReplies =
    generalRepliesState.isFetching ||
    jobRepliesState.isFetching ||
    directRepliesState.isFetching;

  useEffect(() => {
    setLoadedReplies(null);
  }, [message.id]);

  const invalidateMessageLists = () => {
    dispatch(chatApi.util.invalidateTags(getMessageListTags(message)));
  };

  const loadMessageDetail = () => {
    if (!messageDetailState.data && !messageDetailState.isFetching) {
      void loadMessage(message.id);
    }
  };

  const loadRemainingReplies = async () => {
    const params = {
      parent_id: message.id,
      page: 1,
      page_size: Math.max(displayedReplyCount, 50),
    };

    try {
      let response;

      if (message.source.conversation_type === 'job_channel') {
        const jobId = message.source.job?.id;
        if (!jobId) {
          throw new Error('Job chat source is unavailable.');
        }

        response = await loadJobReplies({ jobId, params }).unwrap();
      } else if (message.source.conversation_type === 'direct_message') {
        response = await loadDirectReplies({
          conversationId: message.source.id,
          params,
        }).unwrap();
      } else if (message.source.conversation_type === 'general_channel') {
        response = await loadGeneralReplies(params).unwrap();
      } else {
        throw new Error('Thread replies are unavailable for this chat source.');
      }

      setLoadedReplies(response.data?.results ?? []);
    } catch (error) {
      notify({ type: 'error', message: getErrorMessage(error) });
    }
  };

  const copyMessage = async () => {
    try {
      await writeMessageToClipboard(detail ?? message);
      notify({ type: 'success', message: 'Message copied.' });
    } catch {
      notify({ type: 'error', message: 'Unable to copy message.' });
    }
  };

  const startEditing = () => {
    if (!canManageMessage) {
      notify({
        type: 'error',
        message: 'You cannot edit this message.',
      });
      return;
    }

    setEditValue(getEditableMessageBody(detail ?? message));
    setIsEditing(true);
  };

  const saveEdit = async (value: RichTextEditorValue) => {
    if (value.isEmpty) {
      return;
    }

    if (hasInvalidWorkReferenceToken(value.canonicalHtml)) {
      notify({
        type: 'error',
        message: 'Choose work references from the suggestions list.',
      });
      return;
    }

    try {
      await updateMessage({
        messageId: message.id,
        payload: {
          body: value.canonicalHtml,
          body_format: 'html',
        },
      }).unwrap();
      setIsEditing(false);
      invalidateMessageLists();
      notify({ type: 'success', message: 'Message updated.' });
    } catch (error) {
      notify({ type: 'error', message: getErrorMessage(error) });
    }
  };

  const confirmDelete = async () => {
    try {
      await deleteMessage({ messageId: message.id }).unwrap();
      setIsDeleteOpen(false);
      invalidateMessageLists();
      notify({ type: 'success', message: 'Message deleted.' });
    } catch (error) {
      notify({ type: 'error', message: getErrorMessage(error) });
    }
  };

  return (
    <article className={styles.chatPanel__message}>
      <Avatar
        size="small"
        name={authorName}
        src={message.author_user?.avatar_url ?? message.author_user?.avatar}
      />
      <div className={styles.chatPanel__messageContent}>
        <div className={styles.chatPanel__messageMeta}>
          {message.author_user ? (
            <WorkspaceMemberHoverCard
              user={message.author_user}
              canMessage={!isCurrentUserAuthor}
              onMessage={isCurrentUserAuthor ? undefined : onOpenDirectMessage}
            >
              <strong>{authorName}</strong>
            </WorkspaceMemberHoverCard>
          ) : (
            <strong>{authorName}</strong>
          )}
          <span>{formatDate(message.created_at, 'h:mm A')}</span>
          {showSource ? (
            onSelectSource ? (
              <button
                type="button"
                className={styles.chatPanel__messageSource}
                onClick={() => onSelectSource(getSourceActiveChat(message))}
              >
                {sourceLabel}
              </button>
            ) : (
              <span className={styles.chatPanel__messageSource}>
                {sourceLabel}
              </span>
            )
          ) : null}
          {message.state === 'edited' ? <span>Edited</span> : null}
          <div className={styles.chatPanel__messageOptions}>
            <OptionsMenu
              ariaLabel="Message actions"
              onOpen={loadMessageDetail}
              actions={[
                {
                  key: 'copy',
                  label: 'Copy',
                  onSelect: copyMessage,
                },
                {
                  key: 'edit',
                  label: 'Edit',
                  loading: messageDetailState.isFetching,
                  disabled: !canManageMessage,
                  disabledText: 'Only the author can edit this message.',
                  onSelect: startEditing,
                },
                {
                  key: 'delete',
                  label: 'Delete',
                  tone: 'danger',
                  loading: messageDetailState.isFetching,
                  disabled: !canManageMessage,
                  disabledText: 'Only the author can delete this message.',
                  onSelect: () => setIsDeleteOpen(true),
                },
              ]}
            />
          </div>
        </div>
        {isEditing ? (
          <div className={styles.chatPanel__messageEditor}>
            <RichTextEditor
              value={editValue}
              autoFocus
              showSubmitButton
              submitAriaLabel="Save message"
              isSubmitDisabled={updateMessageState.isLoading}
              footerRight="Edit mode"
              onChange={(value) => setEditValue(value.html)}
              onCancel={() => setIsEditing(false)}
              onSubmit={saveEdit}
            />
          </div>
        ) : (
          <div
            className={clsx(
              styles.chatPanel__messageBody,
              isDeleted && styles.chatPanel__deletedMessageBody,
            )}
            onClick={(event) =>
              handleUserMentionClick({ event, onOpenUserProfile })
            }
            dangerouslySetInnerHTML={{ __html: html }}
          />
        )}

        {replies.length ? (
          <div className={styles.chatPanel__replyPreviewList}>
            {replies.map((reply) => (
              <ChatReplyPreview
                key={reply.id}
                message={reply}
                currentUserId={currentUserId}
                currentUserEmail={currentUserEmail}
                onOpenDirectMessage={onOpenDirectMessage}
                onOpenUserProfile={onOpenUserProfile}
              />
            ))}
          </div>
        ) : null}

        <div className={styles.chatPanel__messageActions}>
          {!isDeleted ? (
            <button
              type="button"
              className={styles.chatPanel__replyButton}
              onClick={() => onReply(message)}
            >
              Reply
            </button>
          ) : null}
          {displayedReplyCount > 0 ? (
            <span>
              {displayedReplyCount}{' '}
              {displayedReplyCount === 1 ? 'reply' : 'replies'}
            </span>
          ) : null}
          {remainingReplyCount > 0 ? (
            <button
              type="button"
              className={styles.chatPanel__replyButton}
              disabled={isLoadingReplies}
              onClick={() => {
                void loadRemainingReplies();
              }}
            >
              {isLoadingReplies
                ? 'Loading replies...'
                : `Load ${remainingReplyCount} more ${
                    remainingReplyCount === 1 ? 'reply' : 'replies'
                  }`}
            </button>
          ) : null}
        </div>
      </div>

      <ConfirmActionModal
        open={isDeleteOpen}
        title="Delete message?"
        description="This removes the message from the conversation while preserving history."
        confirmButtonLabel="Delete"
        confirmButtonVariant="error"
        requireConfirmationText={false}
        isProcessing={deleteMessageState.isLoading}
        onConfirm={confirmDelete}
        onClose={() => setIsDeleteOpen(false)}
      />
    </article>
  );
}
